import React, { useEffect, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

interface PageTransitionProps {
  children: React.ReactNode;
  transitionKey: string;
  className?: string;
}

/**
 * PageTransition component provides smooth fade and slide transitions for page content.
 *
 * Animates content with:
 * - Fade out: opacity 1 → 0
 * - Slide up: translateY 0 → -16px
 * - Fade in: opacity 0 → 1
 * - Slide down: translateY 16px → 0
 *
 * The header in AppLayout remains static - only the content inside <main> animates.
 */
export function PageTransition({ children, transitionKey, className }: PageTransitionProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // For users with reduced motion preference, show content immediately
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    // Trigger animation on mount/key change
    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [transitionKey]);

  return (
    <div
      className={cn(
        'flex flex-col h-full',
        'transition-all duration-300 ease-in-out will-change-[opacity,transform]',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
        className,
      )}
    >
      {children}
    </div>
  );
}
